import { useState } from 'react';
import { Priority } from '../types';
import { useMarketingData } from '../MarketingContext';

export default function SEOPage() {
  const { companies, products, keywords, addKeyword } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [showAdd, setShowAdd] = useState(false);

  const filtered = keywords.filter(kw => {
    if (search && !kw.keyword.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterPriority !== 'all' && kw.priority !== filterPriority) return false;
    if (filterCompany !== 'all' && kw.companyId !== filterCompany) return false;
    return true;
  });

  const totalVolume = keywords.reduce((s, k) => s + k.volume, 0);

  const top10 = keywords.filter(k => k.currentRank > 0 && k.currentRank <= 10);
  const avgDifficulty = keywords.length > 0 ? Math.round(keywords.reduce((s, k) => s + k.difficulty, 0) / keywords.length) : 0;

  const priorityColors: Record<string, string> = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-blue-100 text-blue-700', low: 'bg-gray-100 text-gray-500' };
  const statusColors: Record<string, string> = { not_started: 'bg-gray-100 text-gray-500', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', active: 'bg-green-100 text-green-700' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">SEO Center</h1><p className="text-sm text-gray-500">Keyword research, ranking tracking, and SEO task management</p></div>
        <button onClick={() => setShowAdd(!showAdd)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">+ Add Keyword</button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 font-medium">Total Keywords</div>
          <div className="text-2xl font-bold text-gray-900">{keywords.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 font-medium">Total Search Volume</div>
          <div className="text-2xl font-bold text-gray-900">{totalVolume.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-green-600 font-medium">Top 10 Rankings</div>
          <div className="text-2xl font-bold text-green-700">{top10.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 font-medium">Avg. Difficulty</div>
          <div className="text-2xl font-bold text-gray-900">{avgDifficulty}<span className="text-sm text-gray-400">/100</span></div>
        </div>
      </div>

      {/* Add Keyword Form */}
      {showAdd && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Add New Keyword</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input id="new-kw" placeholder="Keyword" className="text-sm border border-gray-200 rounded-lg px-3 py-2" />
            <input id="new-kw-volume" type="number" placeholder="Search Volume" className="text-sm border border-gray-200 rounded-lg px-3 py-2" />
            <input id="new-kw-difficulty" type="number" placeholder="Difficulty (0-100)" className="text-sm border border-gray-200 rounded-lg px-3 py-2" />
            <select id="new-kw-priority" className="text-sm border border-gray-200 rounded-lg px-3 py-2"><option value="medium">Medium</option><option value="high">High</option><option value="low">Low</option></select>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg">Cancel</button>
            <button onClick={() => {
              const kw = (document.getElementById('new-kw') as HTMLInputElement)?.value;
              if (kw) {
                addKeyword({ id: 'kw-' + Date.now(), keyword: kw, companyId: companies[0]?.id || '', productId: '', targetPage: '', intent: 'informational', volume: Number((document.getElementById('new-kw-volume') as HTMLInputElement)?.value) || 0, competition: 'medium', difficulty: Number((document.getElementById('new-kw-difficulty') as HTMLInputElement)?.value) || 50, cpc: 0, funnelStage: 'consideration', currentRank: 0, targetRank: 10, priority: (document.getElementById('new-kw-priority') as HTMLSelectElement)?.value as Priority || 'medium', owner: '', status: 'not_started', lastReviewed: new Date().toISOString().split('T')[0] });
                setShowAdd(false);
              }
            }} className="px-4 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg">Add</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search keywords..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Priority</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
      </div>

      {/* Keyword Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Keyword</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Volume</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Difficulty</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Intent</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Rank</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Target</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Priority</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(kw => {
              const prod = products.find(p => p.id === kw.productId);
              return (
                <tr key={kw.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{kw.keyword}</div>
                    <div className="text-xs text-gray-500">{kw.targetPage} {prod && `· ${prod.name}`}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{kw.volume.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${kw.difficulty > 60 ? 'bg-red-400' : kw.difficulty > 30 ? 'bg-amber-400' : 'bg-green-400'}`} style={{ width: `${kw.difficulty}%` }} /></div>
                      <span className="text-xs text-gray-500">{kw.difficulty}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">{kw.intent}</td>
                  <td className="px-4 py-3"><span className={`text-sm font-bold ${kw.currentRank > 0 ? (kw.currentRank <= 10 ? 'text-green-600' : 'text-amber-600') : 'text-red-500'}`}>{kw.currentRank > 0 ? `#${kw.currentRank}` : '—'}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-600">#{kw.targetRank}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[kw.priority]}`}>{kw.priority}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[kw.status] || 'bg-gray-100 text-gray-500'}`}>{kw.status.replace('_', ' ')}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
